// Service pour la gestion des événements (CRUD)

import { saveItem, getAllItems, deleteItem, getStoreNames, initDB } from './dbService.js';

const EVENTS_STORE = getStoreNames().EVENTS;

/**
 * Sauvegarde les événements dans IndexedDB
 * @param {Array} events - Les événements à sauvegarder
 */
async function saveEvents(events) {
  try {
    // Utiliser les fonctions de dbService pour une cohérence dans l'accès à IndexedDB
    const db = await initDB(); // Importer initDB depuis dbService.js
    const transaction = db.transaction(EVENTS_STORE, 'readwrite');
    const store = transaction.objectStore(EVENTS_STORE);

    // Effacer tous les événements existants
    await new Promise((resolve, reject) => {
      const clearRequest = store.clear();
      clearRequest.onsuccess = () => resolve();
      clearRequest.onerror = () => reject(clearRequest.error);
    });

    // Ajouter les événements un par un
    const promises = events.map(event => {
      // Supprimer isInPeriod car c'est une propriété calculée
      const eventToStore = { ...event };
      delete eventToStore.isInPeriod;

      return new Promise((resolve, reject) => {
        const addRequest = store.add(eventToStore);
        addRequest.onsuccess = () => resolve();
        addRequest.onerror = () => reject(addRequest.error);
      });
    });

    await Promise.all(promises);
    return true;
  } catch (error) {
    console.error('Erreur lors de la sauvegarde des événements:', error);
    throw error;
  }
}

// Charger les événements depuis IndexedDB
export async function loadEvents() {
  try {
    return await getAllItems(EVENTS_STORE);
  } catch (error) {
    console.error('Erreur lors du chargement des événements:', error);
    return [];
  }
}

/**
 * Ajoute un nouvel événement
 * @param {Object} event - L'événement à ajouter
 * @returns {Promise<Object>} L'événement ajouté
 */
export async function addEvent(event) {
  try {
    // S'assurer que l'événement a un ID
    if (!event.id) {
      event.id = Date.now();
    }

    // Assurer que les types de données sont corrects
    event.hours = parseInt(event.hours, 10); // Utiliser parseInt au lieu de parseFloat

    // Faire une copie de l'objet pour éviter des problèmes de référence
    const eventToSave = { ...event };

    // Enregistrer et retourner l'événement
    return await saveItem(EVENTS_STORE, eventToSave);
  } catch (error) {
    console.error('Erreur dans addEvent:', error);
    throw error; // Rethrow pour que l'erreur soit capturée par le gestionnaire d'événements
  }
}

/**
 * Met à jour un événement existant
 * @param {number|string} id - L'ID de l'événement à mettre à jour
 * @param {Object} updatedEvent - L'événement avec les modifications
 * @returns {Promise<Object>} L'événement mis à jour
 */
export async function updateEvent(id, updatedEvent) {
  try {
    // Vérifier que l'ID correspond bien à l'ID de l'événement
    if (id !== updatedEvent.id) {
      updatedEvent.id = id;
    }

    // Supprimer isInPeriod car c'est une propriété calculée
    const eventToStore = { ...updatedEvent };
    delete eventToStore.isInPeriod;

    console.log('Mise à jour de l\'événement avec ID:', id, eventToStore);

    // Utiliser saveItem pour mettre à jour l'événement
    const result = await saveItem(EVENTS_STORE, eventToStore);
    console.log('Événement mis à jour:', result);

    return result;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de l\'événement:', error);
    throw error;
  }
}

// Supprimer un événement
export async function deleteEvent(eventId) {
  try {
    await deleteItem(EVENTS_STORE, eventId);
    return await loadEvents();
  } catch (error) {
    console.error('Erreur lors de la suppression d\'un événement:', error);
    return [];
  }
}

// Assurez-vous d'exporter saveEvents si d'autres modules en ont besoin
export { saveEvents };